import Button from "@imminently/decisively-ui/Button";
import clsx from "clsx";
import { ErrorBoundary } from "react-error-boundary";
import { Check, Circle, CircleAlert, Play, TriangleAlert, type LucideIcon } from "lucide-react";

import { adapterStepProgressLabel, type AdapterStepProgressState } from "@/features/adapters/stepProgress";

export interface ManagedResourceBadgeProps {
  className?: string;
  currentStep?: boolean;
  disabled?: boolean;
  onToggleGuide: () => void;
  status: AdapterStepProgressState;
  stepName: string;
}

const statusIcon: Record<AdapterStepProgressState, LucideIcon> = {
  success: Check,
  "in-progress": Play,
  warning: TriangleAlert,
  destructive: CircleAlert,
  "not-started": Circle,
};

const statusColor: Record<AdapterStepProgressState, string> = {
  success: "text-[var(--success)]",
  "in-progress": "text-primary",
  warning: "text-warning",
  destructive: "text-destructive",
  "not-started": "text-white/40",
};

const ManagedResourceBadgeContent = ({
  className = "",
  currentStep = false,
  disabled = false,
  onToggleGuide,
  status,
  stepName,
}: ManagedResourceBadgeProps) => {
  const StatusIcon = statusIcon[status];

  return (
    <Button
      label={`${currentStep ? "Close" : "Open"} Adapter guide at ${stepName}: ${adapterStepProgressLabel[status]}`}
      variant="outline"
      size="sm"
      selected={currentStep}
      className={clsx(
        "h-auto min-w-0 max-w-72 !border-neutral-800 !bg-neutral-950 py-1.5 !text-white hover:!bg-neutral-800 hover:!text-white",
        className,
      )}
      disabled={disabled}
      onClick={onToggleGuide}
      aria-pressed={currentStep}
      data-current-step={currentStep || undefined}
      data-step-status={status}
    >
      <StatusIcon
        className={`h-4 w-4 shrink-0 ${statusColor[status]}`}
        aria-hidden="true"
        data-testid="managed-resource-step-status"
      />
      <span className="min-w-0 max-w-56 text-left leading-tight">
        <span
          className="block text-[10px] font-medium tracking-wide text-white/60"
          data-testid="managed-resource-adapter-label"
        >
          Adapter Managed
        </span>
        <span className="block truncate font-medium">{stepName}</span>
      </span>
    </Button>
  );
};

const onBadgeError = (error: Error, info: { componentStack?: string | null }) => {
  console.error("Adapter managed resource badge failed to render", error, info.componentStack);
};

/** The badge, or nothing if it fails to render: it is extra information and must never take down the page it's on. */
export const ManagedResourceBadge = (props: ManagedResourceBadgeProps) => (
  <ErrorBoundary fallback={null} onError={onBadgeError} resetKeys={[props.status, props.stepName]}>
    <ManagedResourceBadgeContent {...props} />
  </ErrorBoundary>
);
